import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  ProviderToken,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { SyllabusQuery } from '@examdojo/category';
import { ImageChatRealtimeService, provideImageChat } from '@examdojo/chat';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { PdfViewerComponent } from '@examdojo/core/pdf-viewer';
import { shareOneReplay } from '@examdojo/core/rxjs';
import { ToastService } from '@examdojo/core/toast';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { isAnyEditingAllowed } from '@examdojo/models/question-editor';
import {
  getFormData,
  JsonEditorTriggerDirective,
  NewQuestionItemButtonComponent,
  provideQuestionItemsForm,
  QuestionActiveItemEditorComponent,
  QuestionItemsEditorComponent,
  QuestionValidationHeaderComponent,
} from '@examdojo/question';
import { ReferenceQuestionQuery, ReferenceQuestionService } from '@examdojo/reference-question';
import {
  QuestionReviewFeedbackFormState,
  QuestionType,
  ReferenceQuestionReviewType,
  ReferenceQuestionReviewTypes,
  ReviewItemService,
  SubmitButtonText,
} from '@examdojo/review';
import { RawSourceItem, SourceQuery, SourceService } from '@examdojo/source';
import { DbJson } from '@examdojo/supabase';
import { LoaderComponent } from '@examdojo/ui/loader';
import { MarkdownViewerComponent } from '@examdojo/ui/markdown-viewer';
import { UserQuery } from '@examdojo/user';
import { AngularSplitModule } from 'angular-split';
import isEqual from 'lodash/isEqual';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  forkJoin,
  map,
  merge,
  of,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { ActiveModeService } from '../../storage/active-mode-storage-manager';
import { ReviewItemImageChatEffectService } from '../effects/review-item-image-chat-effect.service';
import { QuestionItemsCachingService } from '../question-items-caching/question-items-caching.service';
import { QuestionItemsStorageService } from '../question-items-storage-service';
import {
  CreatingReviewItemBackendError,
  QuestionValidationService,
  ReviewConfig,
} from '../question-validation.service';
import { SkipQuestionDialogService } from '../skip-question-dialog/skip-question-dialog.service';
import { ReferenceQuestionEditorItemsFormService } from './ref-question-editor-items-form-service';
import { getReferenceQuestionReviewConfig } from './ref-question-validation.model';
import { ReferenceQuestionSidebarComponent } from './reference-question-sidebar/reference-question-sidebar.component';

@Component({
  selector: 'dojo-ref-question-validation',
  templateUrl: './ref-question-validation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AngularSplitModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    PdfViewerComponent,
    LoaderComponent,
    QuestionItemsEditorComponent,
    QuestionActiveItemEditorComponent,
    ButtonComponent,
    ReactiveFormsModule,
    FormsModule,
    ReferenceQuestionSidebarComponent,
    IconButtonComponent,
    NewQuestionItemButtonComponent,
    JsonEditorTriggerDirective,
    MatTooltip,
    MatSlideToggleModule,
    MarkdownViewerComponent,
    QuestionValidationHeaderComponent,
    IconComponent,
  ],
  providers: [
    provideQuestionItemsForm(ReferenceQuestionEditorItemsFormService),
    provideImageChat(),
    ReviewItemImageChatEffectService,
    SkipQuestionDialogService,
    QuestionValidationService,
    QuestionItemsStorageService,
    QuestionItemsCachingService,
  ],
  host: {
    class: 'flex flex-col h-full',
  },
})
export class RefQuestionValidationComponent {
  constructor(
    private readonly route: ActivatedRoute,
    private readonly questionValidationService: QuestionValidationService,
    private readonly referenceQuestionService: ReferenceQuestionService,
    private readonly referenceQuestionQuery: ReferenceQuestionQuery,
    private readonly sourceService: SourceService,
    private readonly sourceQuery: SourceQuery,
    private readonly destroyRef: DestroyRef,
    private readonly reviewItemService: ReviewItemService,
    private readonly referenceQuestionEditorItemsFormService: ReferenceQuestionEditorItemsFormService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly activeModeService: ActiveModeService,
    private readonly skipQuestionDialogService: SkipQuestionDialogService,
    private readonly userQuery: UserQuery,
    private readonly questionItemsStorageService: QuestionItemsStorageService,
  ) {
    merge(
      this.fetchRefQuestionOnIdChange(),
      this.resetFeedbackOnRefQuestionIdChange(),
      this.handleSyllabusChange(),
      this.scrollToReferenceQuestionOnRefQuestionChange(),
      this.handleModeChange(),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  private readonly instantiatedServices = [ReviewItemImageChatEffectService, ImageChatRealtimeService].map(
    (service: ProviderToken<unknown>) => inject(service),
  );

  readonly activeReferenceQuestionId$ = this.questionValidationService.activeReviewItem$.pipe(
    map((reviewItem) => reviewItem?.reference_question_id),
    distinctUntilChanged(),
  );

  readonly sourcePreviewPlace = signal<RawSourceItem['type'] | 'raw-markscheme'>('questions');

  readonly formState = signal<QuestionReviewFeedbackFormState | null>(null);

  readonly mode$ = this.route.params.pipe(
    map((params) => {
      const mode = params['mode'] as ReferenceQuestionReviewType;
      if (ReferenceQuestionReviewTypes.includes(mode)) {
        return mode;
      }
      return null;
    }),
  );

  readonly storeInBraintrust = signal(false);
  readonly brainTrustDataset = signal('ib-reference-markscheme');

  readonly showPdfViewer = signal(true);
  readonly pdfViewer = viewChild<PdfViewerComponent>('pdfViewer');
  readonly pdfViewer$ = toObservable(this.pdfViewer).pipe(filter(isNotNullish));

  readonly sidebarComponent = viewChild<ReferenceQuestionSidebarComponent>(ReferenceQuestionSidebarComponent);

  readonly CreatingReviewItemBackendError = CreatingReviewItemBackendError;
  readonly SubmitButtonText = SubmitButtonText;

  readonly activeReferenceQuestion$ = this.activeReferenceQuestionId$.pipe(
    switchMap((refQuestionId) => (refQuestionId ? this.referenceQuestionQuery.selectEntity(refQuestionId) : of(null))),
  );

  readonly source$ = this.activeReferenceQuestion$.pipe(
    switchMap((refQuestion) =>
      refQuestion?.source_id ? this.sourceQuery.selectUIEntity(refQuestion.source_id) : of(null),
    ),
  );

  readonly isSkipping = signal(false);

  readonly pdfNumberMetadata$ = combineLatest([
    this.activeReferenceQuestion$,
    toObservable(this.sourcePreviewPlace),
  ]).pipe(
    map(([refQuestion, selectedPdfPlace]) => {
      if (selectedPdfPlace === 'questions') {
        return refQuestion?.metadata?.pdf_question_page_number;
      }
      return refQuestion?.metadata?.pdf_markscheme_page_number;
    }),
    shareOneReplay(),
    distinctUntilChanged(),
  );

  readonly ReferenceQuestionReviewType = ReferenceQuestionReviewType;
  readonly isSaving$$ = new BehaviorSubject<boolean>(false);

  readonly config$ = this.mode$.pipe(
    map((mode) => (mode ? getReferenceQuestionReviewConfig(mode) : null)),
    startWith(undefined),
  );

  readonly reviewConfig$ = this.mode$.pipe(
    map((mode) => (mode ? ({ type: 'reference', mode } satisfies ReviewConfig) : null)),
  );

  private readonly isQuestionItemsFormValid$ = this.referenceQuestionEditorItemsFormService.editingOptions$.pipe(
    switchMap((editingOptions) => {
      if (editingOptions?.allowMarkschemeStructureEditing) {
        return this.referenceQuestionEditorItemsFormService.form.statusChanges.pipe(
          startWith(this.referenceQuestionEditorItemsFormService.form.status),
          map((status) => status === 'VALID'),
        );
      }
      return of(true);
    }),
  );

  readonly state = connectState({
    creatingReviewItemError: this.questionValidationService.creatingReviewItemError$$,
    creatingReviewItem: this.questionValidationService.creatingReviewItem$$,
    permissionError: this.questionValidationService.permissionError$$,
    source: this.source$,
    rawMarkscheme: combineLatest([this.source$, this.activeReferenceQuestion$]).pipe(
      map(([source, activeRefQuestion]) => {
        if (!activeRefQuestion?.question_number) {
          return null;
        }
        const markschemes = source?.processed_content?.markschemes;
        return markschemes?.[activeRefQuestion.question_number - 1]?.text ?? null;
      }),
    ),
    pdfUrls: this.source$.pipe(
      distinctUntilChanged((a, b) => isEqual(a?.raw_source, b?.raw_source)),
      switchMap((source) =>
        source
          ? forkJoin({
              markschemes: this.sourceService.getRawSourceUrls('markschemes', source),
              questions: this.sourceService.getRawSourceUrls('questions', source),
            })
          : of(null),
      ),
    ),
    mode: this.mode$,
    refQuestion: this.activeReferenceQuestion$,
    activeItem: toObservable(this.referenceQuestionEditorItemsFormService.activeQuestionItem),
    activeSubItem: toObservable(this.referenceQuestionEditorItemsFormService.activeQuestionSubItem),
    isSendingFeedback: this.reviewItemService.isSendingFeedback$$,
    config: this.config$,
    isSaving: this.isSaving$$,
    canUndo: this.referenceQuestionEditorItemsFormService.canUndo$,
    canRedo: this.referenceQuestionEditorItemsFormService.canRedo$,
    isQuestionItemsFormValid: this.isQuestionItemsFormValid$,
    pdfNumber: this.pdfNumberMetadata$,
    formState: toObservable(this.formState),
    isAdmin: this.userQuery.selectHasRole('ADMIN'),
    anyEditingAllowed: this.config$.pipe(map(isAnyEditingAllowed)),
    markCalculationMethod: this.syllabusQuery.markCalculationMethod$,
    calculatorAllowed: this.activeReferenceQuestion$.pipe(
      map((refQuestion) => refQuestion?.metadata?.calculator_allowed ?? false),
    ),
    isCalculatorEditingAllowed: this.mode$.pipe(
      map(
        (mode) =>
          mode === ReferenceQuestionReviewType.Question ||
          mode === ReferenceQuestionReviewType.Markscheme ||
          mode === ReferenceQuestionReviewType.ExpertMarkscheme,
      ),
    ),
  });

  undo() {
    this.referenceQuestionEditorItemsFormService.undo();
  }
  redo() {
    this.referenceQuestionEditorItemsFormService.redo();
  }

  saveAndContinue() {
    this.isSaving$$.next(true);

    const refQuestionId = this.state.refQuestion?.id;
    assertNonNullable(refQuestionId, 'refQuestionId');

    const originalRefQuestionItems = this.referenceQuestionQuery.getEntity(refQuestionId)?.question_items;

    const feedback = this.formState()?.value?.feedback;
    assertNonNullable(feedback, 'feedback');

    this.referenceQuestionEditorItemsFormService
      .getProcessedFormData()
      .pipe(
        switchMap((processedData) => {
          if (!originalRefQuestionItems || !isEqual(getFormData(originalRefQuestionItems), processedData)) {
            return this.referenceQuestionService.update(refQuestionId, {
              question_items: processedData as unknown as DbJson,
            });
          }

          console.debug('no changes, skipping question update');

          return of(null);
        }),
        switchMap(() => {
          if (this.storeInBraintrust()) {
            return this.referenceQuestionService
              .addToDataset(refQuestionId, this.brainTrustDataset())
              .pipe(switchMap(() => this.reviewItemService.giveReferenceQuestionFeedback(refQuestionId, feedback)));
          } else {
            return this.reviewItemService.giveReferenceQuestionFeedback(refQuestionId, feedback);
          }
        }),
        switchMap(() => this.deleteQuestionState(refQuestionId)),

        switchMap(() => this.reviewConfig$.pipe(first(Boolean))),
        switchMap((reviewConfig) => this.questionValidationService.refreshReviewItem(reviewConfig)),
        this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
        this.errorHandlerService.catchError(
          `[RefQuestionValidationComponent.saveAndContinue]: failed to save feedback`,
          () => EMPTY,
          {
            toast: `Image upload failed, please contact us`,
            context: {
              refQuestionId: this.state.refQuestion?.id,
            },
          },
        ),
        finalize(() => this.isSaving$$.next(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  scrollToReferenceQuestion() {
    this.pdfNumberMetadata$
      .pipe(
        first(),
        filter(isNotNullish),
        switchMap((pageNumber) => {
          return this.pdfViewer$.pipe(
            first(),
            tap((pdfViewer) => pdfViewer.setPage(pageNumber)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  async openSkipQuestionDialog() {
    const result = await this.skipQuestionDialogService.openDialog();
    if (result && !this.isSkipping()) {
      this.skipReviewItem();
    }
  }

  skipReviewItem() {
    const refQuestionId = this.state.refQuestion?.id;
    assertNonNullable(refQuestionId, 'refQuestionId');

    this.isSkipping.set(true);
    this.questionValidationService
      .skipReviewItem()
      .pipe(
        switchMap(() => this.deleteQuestionState(refQuestionId)),
        switchMap(() => this.reviewConfig$.pipe(first(Boolean))),
        switchMap((reviewConfig) => this.questionValidationService.refreshReviewItem(reviewConfig)),
        finalize(() => this.isSkipping.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onBrainTrustDatasetChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target) {
      this.brainTrustDataset.set(target.value);
    }
  }

  onCalculatorAllowedChange(allowed: boolean): void {
    const refQuestionId = this.state.refQuestion?.id;
    if (!refQuestionId) {
      return;
    }

    const currentMetadata = this.state.refQuestion?.metadata;
    this.referenceQuestionService
      .update(refQuestionId, {
        metadata: {
          ...(currentMetadata ?? {}),
          calculator_allowed: allowed,
        } as DbJson,
      })
      .pipe(
        tap(() => {
          this.toastService.success('Calculator setting updated');
        }),
      )
      .subscribe();
  }

  private deleteQuestionState(refQuestionId: number) {
    assertNonNullable(this.state.mode, 'mode');
    return this.questionItemsStorageService.deleteQuestionState({
      questionType: QuestionType.Reference,
      activeMode: this.state.mode,
      activeQuestionId: refQuestionId,
    });
  }

  private fetchRefQuestionOnIdChange() {
    return this.activeReferenceQuestionId$.pipe(
      filter(isNotNullish),
      switchMap((refQuestionId) => this.referenceQuestionService.fetch(refQuestionId)),
      switchMap((refQuestion) => (refQuestion?.source_id ? this.sourceService.fetch(refQuestion.source_id) : of(null))),
      this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
    );
  }

  private resetFeedbackOnRefQuestionIdChange() {
    return this.activeReferenceQuestionId$.pipe(
      filter(isNotNullish),
      tap(() => this.formState.set(null)),
    );
  }

  private handleSyllabusChange() {
    return this.syllabusQuery.selectActiveId().pipe(
      filter(isNotNullish),
      distinctUntilChanged(),
      skip(1),
      tap(() => this.referenceQuestionService.resetActiveReferenceQuestion()),
      switchMap(() => this.reviewConfig$.pipe(first(Boolean))),
      switchMap((reviewConfig) => this.questionValidationService.refreshReviewItem(reviewConfig)),
    );
  }

  private scrollToReferenceQuestionOnRefQuestionChange() {
    return merge(
      this.activeReferenceQuestion$.pipe(filter(isNotNullish)),
      toObservable(this.sourcePreviewPlace).pipe(filter((place) => place !== 'raw-markscheme')),
    ).pipe(tap(() => this.scrollToReferenceQuestion()));
  }

  private handleModeChange() {
    return this.mode$.pipe(
      filter(isNotNullish),
      tap((mode) => {
        this.referenceQuestionService.resetActiveReferenceQuestion();
        this.activeModeService.setReferenceQuestionMode(mode);
        this.referenceQuestionEditorItemsFormService.mode$$.next(mode);
      }),
      switchMap((mode) =>
        this.questionValidationService.resetReviewItem({
          type: 'reference',
          mode,
        }),
      ),
    );
  }
}
